import { Grid, Icon, Typography, styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useCallback, useEffect, useMemo } from "react";

// Components
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

import { IDynamicQuery, IUnbookedFlight, IUser } from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";

// Styling
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import classNames from "classnames";
import { searchUnbookedFlights } from "src/redux/actions/CrewTravel/CrewTravel.Actions";
import { defaultSearchStatus, FilterWithDateInput, IEventFilter, returnSelectedOptions } from "./Helpers";
import { convertFilter2Query, unbookedFilterOptions } from "./UnbookedHelper";

// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";

// Airport Retrieval - used for Base ICAO filter.
import { IFilterOption } from "@vmosolutions/oc-interfaces";
import { IAirportOption } from "src/redux/reducers/StructuresReducer"; //IAirportOptionMap
import { airportSearchString, updateAirportOptions } from "src/utils/airportHelper"; //findAirports, filterAirports

const OCUnbookedFilter = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  "&.saved_search_selection": {
    marginLeft: 10,
    width: "auto",
    "& .saved_search_icon": {
      position: "relative",
      width: 24,
      height: 24,
      "& .search_icon": {},
      "& .star_icon": {
        width: 9,
        height: 10,
        top: 4,
        left: 5,
        position: "absolute",
      },
    },
    "& .timeline_event_saved_search_filter": {
      width: 263,
    },
  },
  "& .filter_drop_down_menu": {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    margin: "5px 10px",
    alignItems: "flex-start",
    "& .timeline_event_search_filter": {
      width: 263,
    },
  },
  "& .active_filters": {
    "& .filter": { width: 263 },
    display: "flex",
    marginLeft: 10,
    alignItems: "flex-start",
    flexWrap: "wrap",
    "& .date_range_filter": {
      display: "flex",
      width: 263,
      flexDirection: "column",
      margin: 5,
      "& .from_date, & .to_date": {
        margin: 0,
        "& label": { width: 68 },
      },
      "& .daysofweek_selection": {
        margin: 5,
        "& .input_control label": { width: 68, margin: "0px 10px auto auto" },
        "& .react-select-container": { width: 175 },
      },
    },
    "& button.search_button": {
      margin: 3,
      padding: "2px 8px",
      fontSize: 12,
      fontWeight: 300,
      width: 100,
      "& .search_icon": { fontSize: 18, marginRight: 5 },
    },
    "& .results_match_filters": {
      margin: 3,
      borderRadius: 3,
      width: 26,
      height: 26,
      border: `1px solid ${theme.palette.success.main}`,
      "& path": { fill: theme.palette.success.main },
    },
  },
}));

/**
 * UnbookedFilter component handles the ability to search Unbooked positioning flights
 * @param props -
 */
export const UnbookedFilter = (props: IEventFilter): JSX.Element => {
  const dispatch = useDispatch();
  const {
    dataType,
    dataOutput,
    parentElement,
    hideLabel,
    allowMultiFilter,
    searchStatus,
    showDateRangeRRule,
    setSearchFilters,
    applySavedFilter,
  } = props;

  const crewMembers = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  const [crewMemberOptions, setCrewMemberOptions] = React.useState<ICrewMember[] | IUser[]>([]);
  const [searching, setSearching] = React.useState<boolean>(false);

  // Store filtered events
  const [filteredEvents, setFilteredEvents] = React.useState<IUnbookedFlight[]>([]);

  // Store search and selected filters, compared to verify results match current filters.
  const [searchFilter, setSearchFilter] = React.useState<IFilterOption[]>([]);
  const [selectedFilter, setSelectedFilter] = React.useState<IFilterOption[]>([]);

  const resultsMatchFilter = useMemo(() => {
    return _.isEqual(searchFilter, selectedFilter);
  }, [searchFilter, selectedFilter]);

  // Make sure all filters contain a value before enabling the search button.
  const enableSearch = useMemo(() => {
    let searchValid = true;
    selectedFilter?.forEach((filter) => {
      if (!filter.searchValue || (Array.isArray(filter.searchValue) && filter.searchValue?.length === 0)) {
        searchValid = false; // set search as invalid if any filters have no assigned value.
      }
    });
    return searchValid;
  }, [selectedFilter]);

  const eventType = "Unbooked Flights";

  // If a selected search filter is passed in from saved filters, apply it. - CC
  useEffect(() => {
    if (applySavedFilter?.dataType === "unbookedFlights") {
      setSelectedFilter(applySavedFilter?.filters ?? []);
    }
  }, [applySavedFilter]);

  /**
   * Get crew memebr options if there are no crew members loaded
   * This happens when you don't have the crew sched loaded
   */
  useEffect(() => {
    const getUsersAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.GetPaginate<IUser>("/users");
      if (Array.isArray(response) && response.length > 0) {
        const crewOnly: IUser[] = response?.filter((user: IUser) => !!user.crewInfo);
        setCrewMemberOptions(crewOnly);
      }
    };
    if (!crewMembers.length) {
      getUsersAction();
    }
  }, [crewMembers]);

  // Clear all filters and results. (resets the tool)
  const handleClearAll = useCallback(() => {
    setSearchFilter([]);
    setSelectedFilter([]);
    setFilteredEvents([]);
    // setSavedSearchFilter(null);
  }, []);

  // If data type changes externally , reset tool.
  useEffect(() => {
    handleClearAll();
  }, [dataType, handleClearAll]);

  // Debounced search call.
  // This reduces the possibility of making duplicate APIs calls in quick succession.
  const debouncedSearchUnbookedFlights = _.debounce(async (selectedFilter: any): Promise<void> => {
    const searchPayload: IDynamicQuery = convertFilter2Query(selectedFilter);

    // Only make the api call if there are filters showing in the query.
    if (searchPayload?.queryArgs?.length > 0) {
      searchStatus({
        active: true,
        message: `Searching for requested ${eventType}...`,
      });
      setSearching(true);

      const searchResults = await searchUnbookedFlights(searchPayload);

      // If response isn't an array, empty table so that search status can be shown.
      const newResults = Array.isArray(searchResults) ? searchResults : [];
      setFilteredEvents(newResults);
      // Store current selected filter (clone to ensure no mutation) as search filter.
      setSearchFilter(_.cloneDeep(selectedFilter));
      setSearching(false);

      if (Array.isArray(searchResults)) {
        if (searchResults.length > 0) {
          const status = { ...defaultSearchStatus, lastSearched: new Date() };
          searchStatus(status);
        } else {
          searchStatus({
            active: false,
            message: `No ${eventType} found, please change filter and try again.`,
            lastSearched: new Date(),
          });
        }
      } else {
        const error: { status?: number; statusText?: string } = searchResults;
        //console.log("search error:", error);
        switch (error?.status) {
          case 413: // Response exceeds allowable size
            searchStatus({
              active: false,
              code: 413,
              message: "Response size too large, please narrow search parameters and try again.",
              lastSearched: new Date(),
            });
            break;
          default:
            // Generic error
            searchStatus({
              active: false,
              message: "Something went wrong please try again or contact administrator for assistance.",
              lastSearched: new Date(),
            });
          // code block
        }
      }
    }
  }, 1000);

  // If filtered events change, update parent.
  useEffect(() => {
    if (dataOutput) dataOutput(filteredEvents);
  }, [filteredEvents, dataOutput]);

  const selectionGroupedOptions = useMemo(() => {
    const groupArray = [];
    groupArray.push({
      label: "Unbooked Flight Fields",
      options: unbookedFilterOptions,
    });
    return groupArray;
  }, []);

  /**
   * Accepts the user's input & determines the type of the user's input.
   * @param event -
   */
  const handleSearch = useCallback(
    (key: string, event: any): void => {
      const newFilter = [...selectedFilter];
      const updateFilter = newFilter.find((filter: IFilterOption) => filter.value.includes(key));
      const searchValue = !!event && !!event.target ? event.target.value : event;
      switch (key) {
        case "outTime":
        case "inTime":
          updateFilter.searchValue = event;
          break;
        case "airlineIATA":
        case "shortId": {
          const codeArray = searchValue
            ? searchValue.split(",").map((shortId: string) => shortId.trim().toUpperCase())
            : null;
          updateFilter.searchValue = codeArray;
          break;
        }
        case "flightNumber": {
          const valueArray = searchValue ? searchValue.split(",").map((value: string) => value.trim()) : null;
          updateFilter.searchValue = valueArray;
          break;
        }
        case "onCrewMember":
          updateFilter.searchValue = searchValue?.map((option: any) => option._id);
          break;
        case "origin":
        case "destination":
          updateFilter.searchValue = searchValue?.map((option: any) => option.ident);
          break;
        default:
          updateFilter.searchValue = searchValue;
      }
      searchStatus(defaultSearchStatus);
      setSelectedFilter(newFilter);
    },
    [searchStatus, selectedFilter]
  );

  /**
   * Handles the user's selected filter to search for a flight.
   * @param selectedFilter -
   */
  const handleSelectedFilter = useCallback(
    (newFilters: any): void => {
      const filterArray = !newFilters
        ? []
        : Array.isArray(newFilters)
          ? _.cloneDeep(newFilters)
          : [_.cloneDeep(newFilters)];
      // Carry over any currently selected filter values (required for using multiple filters)
      if (filterArray.length > 0) {
        filterArray?.map((newF: IFilterOption) => {
          const filter = selectedFilter.find((oldF: IFilterOption) => oldF.value === newF.value);
          if (!!filter && !!filter.searchValue) {
            newF.searchValue = filter.searchValue;
          }
          return false;
        });
      }
      searchStatus(defaultSearchStatus);
      setSelectedFilter(filterArray);
    },
    [searchStatus, selectedFilter]
  );

  /**
   * Maps out selected filter array and returns the selected field inputs if selected.
   */
  const renderFilterInputs = useCallback((): JSX.Element => {
    if (!selectedFilter || selectedFilter.length === 0) {
      return null;
    }

    return (
      <>
        {selectedFilter?.map((filter: IFilterOption, index: number) => {
          const { label, value, type } = filter;

          const dateTypes = ["outTime", "inTime"];
          const key = value;

          // Date Fields
          if (dateTypes.includes(key)) {
            return (
              <div key={index}>
                <FilterWithDateInput
                  className="filter"
                  dateTimeRange={filter.searchValue}
                  input={{ type: "text" }}
                  name={label}
                  onChange={(event: any) => handleSearch(key, event)}
                  type="date"
                  showDateRangeRRule={showDateRangeRRule}
                />
              </div>
            );
          } else if (key === "shortId") {
            return (
              <div key={index}>
                <TextField
                  className="auto_width filter table_filter_textfield"
                  label={label}
                  name={key}
                  value={filter.searchValue}
                  onChange={(event: any) => handleSearch(key, event)}
                />
              </div>
            );
          } else if (key === "flightNumber" || key === "airlineIATA") {
            return (
              <div key={index}>
                <TextField
                  className="auto_width filter table_filter_textfield"
                  label={label}
                  name={key}
                  value={filter.searchValue}
                  onChange={(event: any) => handleSearch(key, event)}
                />
              </div>
            );
          } else if (key === "origin" || key === "destination") {
            return (
              <div key={index}>
                <SelectField
                  className="auto_width filter airport_selection"
                  label={label}
                  name={key}
                  value={returnSelectedOptions(Object.values(airportOptions), filter.searchValue, "ident")}
                  isMulti={true}
                  onChange={(airports: IAirportOption[]) => handleSearch(key, airports)}
                  onInputChange={(ident: string) => {
                    if (ident?.length >= 2) {
                      // Only run a search if 2 or more characters exist in search string.
                      updateAirportOptions(ident, airportOptions, dispatch);
                    }
                  }}
                  options={Object.values(airportOptions)}
                  getOptionLabel={(airport: IAirportOption) =>
                    airport ? `${airport.ident}/${airport.iata} - ${airport.name}` : ""
                  }
                  getOptionValue={(airport: IAirportOption) => airport.ident}
                  noOptionsMessage={() => airportSearchString}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          } else if (key === "onCrewMember") {
            return (
              <div key={index}>
                <SelectField
                  className="auto_width filter"
                  label={label}
                  name={key}
                  value={returnSelectedOptions(crewMemberOptions, filter.searchValue, "_id")}
                  isMulti={true}
                  onChange={(ACType: ICrewMember[]) => handleSearch(key, ACType)}
                  options={crewMemberOptions.sort((a, b) => {
                    return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
                  })}
                  getOptionLabel={(option: ICrewMember) =>
                    option?.firstName && option?.lastName
                      ? `${option.lastName}, ${option.firstName} ${option?.employeeId ? `(${option.employeeId})` : ""}`
                      : ""
                  }
                  getOptionValue={(option: ICrewMember) => option._id}
                  noOptionsMessage={() => "no crew options available"}
                  isSearchable={true}
                  isClearable={true}
                  menuPosition="fixed"
                  maxMenuHeight={400}
                  menuPortalTarget={parentElement}
                  openMenuOnFocus={false}
                  blurInputOnSelect={true}
                  closeMenuOnSelect={true}
                  placeholder=""
                />
              </div>
            );
          }

          return null;
        })}
      </>
    );
  }, [airportOptions, crewMemberOptions, dispatch, handleSearch, parentElement, selectedFilter, showDateRangeRRule]);

  return (
    <OCUnbookedFilter className={classNames("search_filters")}>
      {!hideLabel && (
        <Typography variant="subtitle1" className="body_text">
          Find Unbooked Flights by:
        </Typography>
      )}

      <div className="auto_width filter_drop_down_menu">
        {hideLabel && <Icon className="control_icon">filter_list</Icon>}
        <SelectField
          className="timeline_event_search_filter"
          label=""
          name="advanced_search_filters"
          value={selectedFilter}
          options={selectionGroupedOptions}
          onChange={handleSelectedFilter}
          isMulti={allowMultiFilter}
          isClearable={true}
          menuPortalTarget={parentElement}
          menuPosition="fixed"
          maxMenuHeight={400}
          multiMode={allowMultiFilter ? "checkbox" : null}
          placeholder={
            selectedFilter?.length === 0
              ? "Select Filters..."
              : selectedFilter?.length === 1
                ? selectedFilter[0].label
                : "Multiple Filters Selected..."
          }
        />
      </div>

      {selectedFilter?.length > 0 && (
        <div className="active_filters">
          {renderFilterInputs()}
          <Grid container style={{ width: "auto" }}>
            <Button
              id={"search_btn"}
              variant="outlined"
              // color="secondary"
              onClick={() => handleClearAll()}
              disabled={filteredEvents?.length === 0 || selectedFilter?.length === 0}
              className="search_button"
            >
              Clear
            </Button>
            <Button
              id={"search_btn"}
              variant="outlined"
              onClick={() => debouncedSearchUnbookedFlights(selectedFilter)}
              disabled={!enableSearch || searching}
              className="search_button"
            >
              {!searching ? (
                <>
                  <Icon className="search_icon">{resultsMatchFilter ? "refresh" : "search"}</Icon>
                  {resultsMatchFilter ? "Refresh" : "Search"}
                </>
              ) : (
                <>
                  <CircularProgress color="inherit" style={{ width: 14, height: 14, marginRight: 8 }} /> searching
                </>
              )}
            </Button>
            {resultsMatchFilter && (
              <Tooltip enterDelay={1000} placement="top" title="Results match selected filter values">
                <CheckBoxIcon className="results_match_filters" viewBox="0 0 24 24" />
              </Tooltip>
            )}
          </Grid>
        </div>
      )}
    </OCUnbookedFilter>
  );
};
